import { useApolloClient, ApolloClient, ApolloQueryResult } from '@apollo/client';
import { UserRecipientGraphql } from '@communication-center/server';
import { debounce } from '@material-ui/core';
import { WMTableRef } from '@walkme/wm-ui';
import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
    GetRecipientsQuery,
    GetRecipientsForAudiencesCombinationQuery,
    GetRecipientsQueryVariables,
    GetRecipientsDocument,
    RecipientsFilter,
    GetRecipientsForAudiencesCombinationDocument,
    GetRecipientsForAudiencesCombinationQueryVariables
} from '../../../../generated/apollo-hooks';
import { useIsMountedRef } from '../../../helpers/useIsMounted';
import useSelector from '../../../infrastructure/useSelector';
import logger from '../../../services/logger.service';
import { recipientsSearchMinSymbols } from '../consts';
import { useAllRecipiensCount } from '../hooks/recipients';

type Query = {
    page: number;
    search: string;
    limit: number;
    recipientsFilter: RecipientsFilter;
};
export function useRecipientsList({
    getFilter,
    audiencesIds,
    endUsersIds,
    isAllRecipients = false
}: {
    getFilter: (args?: unknown) => RecipientsFilter;
    endUsersIds?: string[];
    audiencesIds?: string[];
    isAllRecipients?: boolean;
}) {
    const environmentId = useSelector(store => store.common.currentEnvironment?.id);
    const tableRef = useRef<WMTableRef>(null);

    const initialSearchValue = useMemo<Query>(() => ({ page: 0, search: '', limit: 50, recipientsFilter: getFilter() }), [getFilter]);

    const [query, setQuery] = useState(initialSearchValue);
    const [recipientsList, setRecipientsList] = useState<UserRecipientGraphql[]>([]);
    const [error, setError] = useState<Error | unknown | null>(null);
    const [recipientsCountResult, setRecipientsCountResult] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const clientSideFiltered = useMemo(() => recipientsList.filter(it => it.label.startsWith(query.search)), [query.search, recipientsList]);
    const apolloClient = useApolloClient();

    const subscription = useRef<$TSFixMe>(null);

    const { data: allRecipientsCount, loading: allRecipientsCountLoading, error: getAllRecipientsSizeError } = useAllRecipiensCount({
        audiencesIds,
        endUsersIds,
        isAllRecipients
    });
    useEffect(() => {
        if (getAllRecipientsSizeError) {
            logger.error('Failed to get all recipients size', getAllRecipientsSizeError);
        }
    }, [getAllRecipientsSizeError]);

    useEffect(
        () => () => {
            if (subscription.current) {
                subscription.current.unsubscribe();
                subscription.current = null;
            }
        },
        []
    );
    const fetchRecipients = useCallback(
        async (audiencesIds: any, endUsersIds: any, query: Query, apolloClient: ApolloClient<Record<string, unknown>>) => {
            async function makeGqlQuery() {
                return new Promise<GetRecipientsQuery | GetRecipientsForAudiencesCombinationQuery>((resolve, reject) => {
                    if (subscription.current) {
                        subscription.current.unsubscribe();
                    }
                    if (audiencesIds?.length > 1 || endUsersIds?.length > 0) {
                        subscription.current = apolloClient.watchQuery<
                            GetRecipientsForAudiencesCombinationQuery,
                            GetRecipientsForAudiencesCombinationQueryVariables
                        >({
                            query: GetRecipientsForAudiencesCombinationDocument,
                            variables: {
                                audiencesIds,
                                endUsersIds,
                                query: { page: query.page, search: query.search, limit: query.limit, recipientsFilter: getFilter() }
                            }
                        });
                    } else if (isAllRecipients || audiencesIds?.length === 1) {
                        subscription.current = apolloClient.watchQuery<GetRecipientsQuery, GetRecipientsQueryVariables>({
                            query: GetRecipientsDocument,
                            variables: {
                                audienceId: isAllRecipients ? '' : audiencesIds[0],
                                query: { page: query.page, search: query.search, limit: query.limit, recipientsFilter: getFilter() }
                            }
                        });
                    } else {
                        throw new Error('audiencesIds and endUsersIds are empty');
                    }
                    subscription.current = subscription.current.subscribe(
                        (data: ApolloQueryResult<GetRecipientsQuery | GetRecipientsForAudiencesCombinationQuery>) => resolve(data.data),
                        (error: unknown) => reject(error)
                    );
                });
            }

            setError(null);
            if (query.page === 0) {
                tableRef.current?.scrollToRow(0, 'start');
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            try {
                const response = await makeGqlQuery();
                const data = 'getRecipients' in response ? response.getRecipients : response.getRecipientsForAudiencesCombination;
                if (data) {
                    setRecipientsCountResult(data.count);
                    if (Array.isArray(data.recipients)) {
                        setRecipientsList(prev => {
                            return query.page === 0 ? [...data.recipients] : [...prev, ...data.recipients];
                        });
                    }
                }
            } catch (err) {
                setError(err);
            }

            setIsLoading(false);
            setIsLoadingMore(false);
        },
        [getFilter, isAllRecipients]
    );

    const retry = () => fetchRecipients(audiencesIds, endUsersIds, query, apolloClient as ApolloClient<Record<string, unknown>>);

    const loadNext = () => {
        setQuery({ ...query, page: query.page + 1 });
    };

    useEffect(() => {
        fetchRecipients(audiencesIds, endUsersIds, query, apolloClient as ApolloClient<Record<string, unknown>>);
    }, [query, apolloClient, fetchRecipients, audiencesIds, endUsersIds]);

    const isMountedRef = useIsMountedRef();
    const onSearch = useMemo(
        () =>
            debounce(async (term: string) => {
                if (!isMountedRef.current) {
                    return;
                }
                if (term.length >= recipientsSearchMinSymbols) {
                    setQuery(_query => ({ ..._query, search: term, page: 0 }));
                } else {
                    setQuery(initialSearchValue);
                }
            }, 500),
        [initialSearchValue, isMountedRef]
    );

    const fetchNextPage = () => {
        if (!isLoading && !isLoadingMore && recipientsList.length < recipientsCountResult) {
            loadNext();
        }
    };
    return {
        isLoading,
        isLoadingMore,
        recipientsList,
        error,
        fetchNextPage,
        onSearch,
        tableRef,
        fetchRecipients,
        retry,
        getAllRecipientsSizeError,
        allRecipientsCountLoading,
        clientSideFiltered,
        environmentId,
        allRecipientsCount,
        query,
        setQuery,
        recipientsCountResult
    };
}
